import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Select, DatePicker, Input, Button, message } from 'antd';
import FormSection from 'components/ui/form-section';
import { ArrowDownIcon } from 'assets/icons/arrow-down';
import CalendarIcon from 'assets/icons/calendar';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import FormContainer from 'components/ui/form-container';
import {
  binderClaimApi,
  claimPaymentsApi,
  countryStateCityApi,
  referenceDataApi,
} from 'services/apis';
import FormLayoutGroup from 'components/ui/form-layout-group';
import { useTranslation } from 'react-i18next';
import { EFormSectionStatus } from 'components/ui/form-section/types';
import { SortedSelect } from 'components/ui/sorted-select';
import { EQueryKeys } from 'constants/query-keys';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { uniqBy } from 'lodash';
import ExcelTable from 'components/ui/excel-table';
import { TOTAL_ROW_KEY } from 'components/ui/excel-table/constants';
import {
  ECellValueType,
  ExcelTableColDef,
} from 'components/ui/excel-table/types';
import TextArea from 'antd/es/input/TextArea';
import { useFileSession } from 'hooks/useFileSession';
import FileUploader from '../file-uploader';
import {
  CreateUploadedFilePayloadDtoEntityTypeEnum,
  CreateClaimPaymentPayloadDto,
  CreateClaimPaymentPayloadDtoTransactionTypeEnum,
  CreateClaimPaymentPayloadDtoPaymentStatusEnum,
  CreateClaimPaymentPayloadDtoClaimsPaymentSourceEnum,
  ClaimPaymentDetailPayloadDto,
  ClaimPaymentDetailPayloadDtoLossTypeEnum,
} from 'libs/pinnacle-sdk';
import { handleError, validateForm } from 'utils/form';
import { constructClaimDetailPath } from 'constants/path';
import { fields, getValidationSchema } from './form';
import dayjs from 'dayjs';
import BigNumber from 'bignumber.js';

// Transaction type enum values
const TRANSACTION_TYPES = {
  PAYMENT: 'Payment',
  RESERVE_CHANGE_ONLY: 'Reserve Change Only',
} as const;

// Loss types for the table
const LOSS_TYPES = [
  ClaimPaymentDetailPayloadDtoLossTypeEnum.Indemnity,
  ClaimPaymentDetailPayloadDtoLossTypeEnum.LossAdjusterCosts,
  ClaimPaymentDetailPayloadDtoLossTypeEnum.ConsultantCosts,
  ClaimPaymentDetailPayloadDtoLossTypeEnum.LegalCosts,
  ClaimPaymentDetailPayloadDtoLossTypeEnum.RiCosts,
];

type LayerResultDisplayedData = {
  id: number;
  lossType: string;
  thisPayment100FGU: number;
  totalPayment100OurLayer: number;
  thisPayment100OurLayer: number;
  thisPaymentGst100OurLayer: number;
  thisPaymentOurShare: number;
  outstanding100OurLayer: number;
  outstandingOurShare: number;
  newIncurred100OurLayer: number;
  newIncurredOurShare: number;
  reserveChangeOurShare: number;
};

const PaymentReserveChange = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { claimId } = useParams<{ claimId: string }>();
  const [searchParams] = useSearchParams();
  const contractId = searchParams.get('contractId');

  // Watch form fields for conditional behavior
  const transactionType = Form.useWatch('transactionType', form);
  const claimsPaymentSource = Form.useWatch('claimsPaymentSource', form);

  // Form validation state
  const [isFormValid, setIsFormValid] = useState(false);

  // State to store payment details from the table
  const [paymentDetails, setPaymentDetails] = useState<
    LayerResultDisplayedData[]
  >([]);

  // Fetch binderClaim data to get contract info (shareOfPremiumSigned, originalCurrency)
  const { data: binderClaim } = useQuery(
    [EQueryKeys.BINDER_CLAIMS, claimId, contractId],
    async () => {
      const result = await binderClaimApi.filterBinderClaims({
        filterBinderClaimsQueryDto: {
          claims: [claimId as string],
          includeContract: true,
          includeSubmission: true,
        },
      });
      // Find the specific binder claim that matches our contractId
      return result.items?.find((item) => item.contractId === contractId);
    },
    {
      enabled: !!claimId && !!contractId,
      refetchOnWindowFocus: false,
    }
  );

  // Fetch bank accounts
  const { data: bankAccounts, isLoading: isLoadingBankAccounts } = useQuery(
    EQueryKeys.LIST_BANK_ACCOUNTS,
    async () => referenceDataApi.getBankAccounts(),
    { refetchOnWindowFocus: false, staleTime: Infinity }
  );

  // Fetch next transaction ID
  const { data: nextTransactionId } = useQuery(
    EQueryKeys.NEXT_CLAIM_PAYMENT_TRANSACTION_ID,
    async () => claimPaymentsApi.getNextTransactionId()
  );

  // Get share of premium signed for calculations
  const shareOfPremiumSigned = useMemo(() => {
    return binderClaim?.contract?.shareOfPremiumSigned
      ? new BigNumber(binderClaim.contract.shareOfPremiumSigned).dividedBy(100)
      : new BigNumber(0);
  }, [binderClaim?.contract?.shareOfPremiumSigned]);

  // Initialize table with loss types when component mounts
  useEffect(() => {
    if (paymentDetails.length === 0) {
      const initialData: LayerResultDisplayedData[] = LOSS_TYPES.map(
        (lossType, index) => ({
          id: index + 1,
          lossType,
          thisPayment100FGU: 0,
          totalPayment100OurLayer: 0,
          thisPayment100OurLayer: 0,
          thisPaymentGst100OurLayer: 0,
          thisPaymentOurShare: 0,
          outstanding100OurLayer: 0,
          outstandingOurShare: 0,
          newIncurred100OurLayer: 0,
          newIncurredOurShare: 0,
          reserveChangeOurShare: 0,
        })
      );
      setPaymentDetails(initialData);
    }
  }, [paymentDetails.length]);

  // Initialize receiving party currency with contract's original currency
  useEffect(() => {
    if (binderClaim?.submission?.originalCurrency) {
      const currentValue = form.getFieldValue('receivingPartyCurreny');
      if (!currentValue) {
        form.setFieldsValue({
          receivingPartyCurreny: binderClaim.submission.originalCurrency,
        });
      }
    }
  }, [binderClaim?.submission?.originalCurrency, form]);

  // Set transaction ID when fetched
  useEffect(() => {
    if (nextTransactionId) {
      form.setFieldsValue({
        transactionId: nextTransactionId,
      });
    }
  }, [nextTransactionId, form]);

  // Calculate values when table data changes
  const handleDataChange = useCallback(
    (_: unknown, allRows: { data: LayerResultDisplayedData[] }) => {
      const updatedData = allRows.data.map((row) => {
        const thisPayment100OurLayer = new BigNumber(
          row.thisPayment100OurLayer || 0
        );
        const outstanding100OurLayer = new BigNumber(
          row.outstanding100OurLayer || 0
        );
        const totalPayment100OurLayer = new BigNumber(
          row.totalPayment100OurLayer || 0
        );

        // This Payment (Our Share) = This Payment (100% Our Layer) * shareOfPremiumSigned
        const thisPaymentOurShare = thisPayment100OurLayer
          .multipliedBy(shareOfPremiumSigned)
          .toNumber();

        // Outstanding (Our Share) = Outstanding (100% Our Layer) * shareOfPremiumSigned
        const outstandingOurShare = outstanding100OurLayer
          .multipliedBy(shareOfPremiumSigned)
          .toNumber();

        // New Incurred (100% Our Layer) = Outstanding (100% Our Layer) + This Payment (100% Our Layer) + Total Payment (100% Our Layer)
        const newIncurred100OurLayer = outstanding100OurLayer
          .plus(thisPayment100OurLayer)
          .plus(totalPayment100OurLayer)
          .toNumber();

        // New Incurred (Our Share) = New Incurred (100% Our Layer) * shareOfPremiumSigned
        const newIncurredOurShare = new BigNumber(newIncurred100OurLayer)
          .multipliedBy(shareOfPremiumSigned)
          .toNumber();

        // Reserve Change (Our Share) = New Incurred (Our Share) - Previous Incurred (Our Share)
        // For now, we'll calculate it as the difference
        const reserveChangeOurShare =
          newIncurredOurShare - (row.newIncurredOurShare || 0);

        return {
          ...row,
          thisPaymentOurShare,
          outstandingOurShare,
          newIncurred100OurLayer,
          newIncurredOurShare,
          reserveChangeOurShare,
        };
      });

      setPaymentDetails(updatedData);
    },
    [shareOfPremiumSigned]
  );

  // Mutation to create claim payment
  const createClaimPaymentMutation = useMutation(
    (payload: CreateClaimPaymentPayloadDto) =>
      claimPaymentsApi.createClaimPayment({
        createClaimPaymentPayloadDto: payload,
      }),
    {
      onSuccess: () => {
        message.success(
          t('forms.createRecordSuccess', {
            type: t('claim.paymentReserveChange.payment'),
          })
        );
        queryClient.invalidateQueries([EQueryKeys.CLAIM_PAYMENTS]);
        // Navigate back to claim detail binder claims summary tab
        navigate(constructClaimDetailPath(claimId as string, '3'));
      },
      onError: async (error: { response: Response }) => {
        handleError({
          error,
          defaultMessage: t('forms.createRecordFail', {
            type: t('claim.paymentReserveChange.payment'),
          }),
        });
      },
    }
  );

  // Handle form submission
  const handleSave = () => {
    form.validateFields().then((values) => {
      if (!claimId || !contractId) {
        message.error(t('claim.paymentReserveChange.missingClaimOrContract'));
        return;
      }

      // Transform payment details from table data to API format
      const claimPaymentDetails: ClaimPaymentDetailPayloadDto[] = paymentDetails
        .filter((detail) => detail.lossType)
        .map((detail) => ({
          lossType: detail.lossType as ClaimPaymentDetailPayloadDtoLossTypeEnum,
          thisPayment100Fgu: detail.thisPayment100FGU?.toString(),
          thisPayment100Layer: detail.thisPayment100OurLayer?.toString(),
          thisPaymentGst100Layer: detail.thisPaymentGst100OurLayer?.toString(),
          outstanding100Layer: detail.outstanding100OurLayer?.toString(),
        }));

      const payload: CreateClaimPaymentPayloadDto = {
        claimId,
        contractId,
        transactionType:
          values.transactionType as CreateClaimPaymentPayloadDtoTransactionTypeEnum,
        transactionDate: values.transactionDate
          ? values.transactionDate.toDate()
          : new Date(),
        paidDate: values.paidDate ? values.paidDate.toDate() : undefined,
        paymentStatus:
          values.paymentStatus as CreateClaimPaymentPayloadDtoPaymentStatusEnum,
        payeeAccountName: values.payeeAccountName,
        country: values.country,
        accountNumber: values.accountNumber,
        swiftCode: values.swiftCode,
        receivingPartyCurrency: values.receivingPartyCurreny,
        settlementCurrency: values.settlementCurrency,
        settlementExchangeRate: values.settlementExchangeRate || '1',
        claimsPaymentSource:
          values.claimsPaymentSource as CreateClaimPaymentPayloadDtoClaimsPaymentSourceEnum,
        premiumBordereauxMonth: values.premiumBordereauxMonth
          ? dayjs(values.premiumBordereauxMonth).format('YYYY-MM')
          : undefined,
        bankAccountNumber: values.fromBankAccount || '',
        commentsTo: values.commentTo,
        comments: values.comment,
        claimPaymentDetails,
      };

      createClaimPaymentMutation.mutate(payload);
    });
  };

  // Handle cancel button
  const handleCancel = () => {
    if (claimId) {
      navigate(constructClaimDetailPath(claimId, '3'));
    } else {
      navigate(-1);
    }
  };

  const { data: countries, isLoading: isLoadingCountries } = useQuery(
    EQueryKeys.COUNTRIES,
    async () => countryStateCityApi.getCountries(),
    { refetchOnWindowFocus: false, staleTime: Infinity }
  );

  const { data: currencies, isLoading: isLoadingCurrencies } = useQuery(
    EQueryKeys.CURRENCIES,
    async () => {
      return referenceDataApi.getCurrencies();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  // Determine if payment fields should be editable based on transaction type
  const isPaymentFieldsEditable = transactionType === TRANSACTION_TYPES.PAYMENT;
  const isOutstandingFieldsEditable = true; // Outstanding fields are always editable

  const columnDefs = useMemo<
    ExcelTableColDef<LayerResultDisplayedData, string | number>[]
  >(() => {
    const columns: ExcelTableColDef<
      LayerResultDisplayedData,
      string | number
    >[] = [
      {
        field: 'lossType',
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'thisPayment100FGU',
        minWidth: 180,
        flex: 1,
        resizable: true,
        editable: isPaymentFieldsEditable,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        field: 'totalPayment100OurLayer',
        minWidth: 200,
        flex: 1,
        resizable: true,
        editable: false,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        field: 'thisPayment100OurLayer',
        minWidth: 200,
        flex: 1,
        resizable: true,
        editable: isPaymentFieldsEditable,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        field: 'thisPaymentGst100OurLayer',
        minWidth: 220,
        flex: 1,
        resizable: true,
        editable: isPaymentFieldsEditable,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        field: 'thisPaymentOurShare',
        minWidth: 180,
        flex: 1,
        resizable: true,
        editable: false,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        field: 'outstanding100OurLayer',
        minWidth: 200,
        flex: 1,
        resizable: true,
        editable: isOutstandingFieldsEditable,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        field: 'outstandingOurShare',
        minWidth: 180,
        flex: 1,
        resizable: true,
        editable: false,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        field: 'newIncurred100OurLayer',
        minWidth: 200,
        flex: 1,
        resizable: true,
        editable: false,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        field: 'newIncurredOurShare',
        minWidth: 180,
        flex: 1,
        resizable: true,
        editable: false,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        field: 'reserveChangeOurShare',
        minWidth: 180,
        flex: 1,
        resizable: true,
        editable: false,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
    ];

    return columns.map((c) => ({
      ...c,
      headerName: t(`claim.paymentReserveChange.table.${c.field}`),
    }));
  }, [t, isPaymentFieldsEditable, isOutstandingFieldsEditable]);

  // Calculate summary row data (totals)
  const summaryRowData = useMemo<LayerResultDisplayedData | undefined>(() => {
    if (paymentDetails.length === 0) {
      return undefined;
    }

    const totals = paymentDetails.reduce(
      (acc, row) => ({
        thisPayment100FGU: acc.thisPayment100FGU + (row.thisPayment100FGU || 0),
        totalPayment100OurLayer:
          acc.totalPayment100OurLayer + (row.totalPayment100OurLayer || 0),
        thisPayment100OurLayer:
          acc.thisPayment100OurLayer + (row.thisPayment100OurLayer || 0),
        thisPaymentGst100OurLayer:
          acc.thisPaymentGst100OurLayer + (row.thisPaymentGst100OurLayer || 0),
        thisPaymentOurShare:
          acc.thisPaymentOurShare + (row.thisPaymentOurShare || 0),
        outstanding100OurLayer:
          acc.outstanding100OurLayer + (row.outstanding100OurLayer || 0),
        outstandingOurShare:
          acc.outstandingOurShare + (row.outstandingOurShare || 0),
        newIncurred100OurLayer:
          acc.newIncurred100OurLayer + (row.newIncurred100OurLayer || 0),
        newIncurredOurShare:
          acc.newIncurredOurShare + (row.newIncurredOurShare || 0),
        reserveChangeOurShare:
          acc.reserveChangeOurShare + (row.reserveChangeOurShare || 0),
      }),
      {
        thisPayment100FGU: 0,
        totalPayment100OurLayer: 0,
        thisPayment100OurLayer: 0,
        thisPaymentGst100OurLayer: 0,
        thisPaymentOurShare: 0,
        outstanding100OurLayer: 0,
        outstandingOurShare: 0,
        newIncurred100OurLayer: 0,
        newIncurredOurShare: 0,
        reserveChangeOurShare: 0,
      }
    );

    return {
      id: TOTAL_ROW_KEY as unknown as number,
      lossType: t('general.total'),
      ...totals,
    };
  }, [paymentDetails, t]);

  const { fileSessionId, fileList, setFileList } = useFileSession();

  return (
    <FormContainer
      title={t('claim.paymentReserveChange.paymentAndOrUpdateReserve')}
    >
      <Form
        form={form}
        layout="vertical"
        className="space-y-8"
        onFinish={() => {}}
        onValuesChange={async () => {
          const values = form.getFieldsValue();
          const isValid = await validateForm(
            values,
            form,
            fields as unknown as (keyof typeof values)[],
            getValidationSchema(t)
          );
          setIsFormValid(isValid);
        }}
      >
        <FormSection
          title={t('claim.paymentReserveChange.claimDetails')}
          collapsible
          status={EFormSectionStatus.FILLING}
        >
          <FormLayoutGroup mdColumns={2}>
            <Form.Item
              name="transactionType"
              label={t('claim.paymentReserveChange.transactionType')}
            >
              <Select
                size="large"
                placeholder={t('general.selectField', {
                  field: t('claim.paymentReserveChange.transactionType'),
                })}
                options={Object.values(TRANSACTION_TYPES).map((type) => ({
                  label: type,
                  value: type,
                }))}
              />
            </Form.Item>

            <Form.Item
              name="transactionDate"
              label={t('claim.paymentReserveChange.transactionDate')}
            >
              <DatePicker
                format={'DD/MM/YYYY'}
                allowClear
                size="large"
                className="w-full"
                suffixIcon={<CalendarIcon />}
                placeholder={t('claim.selectDate')}
              />
            </Form.Item>
          </FormLayoutGroup>

          <FormLayoutGroup mdColumns={3}>
            <Form.Item
              name="transactionId"
              label={t('claim.paymentReserveChange.transactionId')}
            >
              <Input
                disabled
                size="large"
                value={form.getFieldValue('transactionId')}
                className="w-full"
              />
            </Form.Item>

            <Form.Item
              name="paidDate"
              label={t('claim.paymentReserveChange.paidDate')}
            >
              <DatePicker
                format={'DD/MM/YYYY'}
                allowClear
                size="large"
                className="w-full"
                suffixIcon={<CalendarIcon />}
                placeholder={t('claim.selectDate')}
              />
            </Form.Item>

            <Form.Item
              name="paymentStatus"
              label={t('claim.paymentReserveChange.paymentStatus')}
            >
              <Select
                size="large"
                placeholder={t('general.selectField', {
                  field: t('claim.paymentReserveChange.paymentStatus'),
                })}
                options={[
                  'Awaiting Approval',
                  'Approved',
                  'Not Approved',
                  'Paid',
                ].map((type) => ({
                  label: type,
                  value: type,
                }))}
              />
            </Form.Item>
          </FormLayoutGroup>

          <FormLayoutGroup mdColumns={4}>
            <Form.Item
              name="payeeAccountName"
              label={t('claim.paymentReserveChange.payeeAccountName')}
            >
              <Input
                size="large"
                value={form.getFieldValue('payeeAccountName')}
                className="w-full"
                placeholder={t('general.enter', {
                  field: t('claim.paymentReserveChange.payeeAccountName'),
                })}
              />
            </Form.Item>

            <Form.Item
              name="country"
              label={t('claim.paymentReserveChange.country')}
            >
              <SortedSelect
                allowClear
                showSearch
                size="large"
                loading={isLoadingCountries}
                placeholder={t('general.selectField', {
                  field: t('general.country'),
                })}
                suffixIcon={<ArrowDownIcon />}
                className="w-full"
                options={uniqBy(countries || [], (country) => country.name).map(
                  (country) => ({
                    value: country.name,
                    label: `${country.emoji} ${country.name}`,
                    key: country.id,
                  })
                )}
                optionFilterProp="value"
              />
            </Form.Item>

            <Form.Item
              name="accountNumber"
              label={t('claim.paymentReserveChange.accountNumber')}
            >
              <Input
                size="large"
                value={form.getFieldValue('accountNumber')}
                className="w-full"
                placeholder={t('general.enter', {
                  field: t('claim.paymentReserveChange.accountNumber'),
                })}
              />
            </Form.Item>

            <Form.Item
              name="swiftCode"
              label={t('claim.paymentReserveChange.swiftCode')}
            >
              <Input
                size="large"
                value={form.getFieldValue('swiftCode')}
                className="w-full"
                placeholder={t('general.enter', {
                  field: t('claim.paymentReserveChange.swiftCode'),
                })}
              />
            </Form.Item>
          </FormLayoutGroup>

          <FormLayoutGroup mdColumns={4}>
            <Form.Item
              name="receivingPartyCurreny"
              label={t('claim.paymentReserveChange.receivingPartyCurreny')}
            >
              <SortedSelect
                suffixIcon={<ArrowDownIcon />}
                allowClear
                size="large"
                showSearch
                placeholder={t('general.selectField', {
                  field: t('claim.paymentReserveChange.receivingPartyCurreny'),
                })}
                loading={isLoadingCurrencies}
                options={(currencies || []).map((currency) => ({
                  label: `${currency.name} (${currency.code})`,
                  value: currency.code,
                }))}
              />
            </Form.Item>

            <Form.Item
              name="originalCurrencyExchangeRate"
              label={t(
                'claim.paymentReserveChange.originalCurrencyExchangeRate'
              )}
            >
              <Input
                size="large"
                value={form.getFieldValue('originalCurrencyExchangeRate')}
                className="w-full"
                placeholder={t('general.enter', {
                  field: t(
                    'claim.paymentReserveChange.originalCurrencyExchangeRate'
                  ),
                })}
              />
            </Form.Item>

            <Form.Item
              name="settlementCurrency"
              label={t('claim.paymentReserveChange.settlementCurrency')}
            >
              <SortedSelect
                suffixIcon={<ArrowDownIcon />}
                allowClear
                size="large"
                showSearch
                placeholder={t('general.selectField', {
                  field: t('claim.paymentReserveChange.settlementCurrency'),
                })}
                loading={isLoadingCurrencies}
                options={(currencies || []).map((currency) => ({
                  label: `${currency.name} (${currency.code})`,
                  value: currency.code,
                }))}
              />
            </Form.Item>

            <Form.Item
              name="settlementExchangeRate"
              label={t('claim.paymentReserveChange.settlementExchangeRate')}
            >
              <Input
                size="large"
                type="number"
                value={form.getFieldValue('settlementExchangeRate')}
                className="w-full"
                placeholder={t('general.enter', {
                  field: t('claim.paymentReserveChange.settlementExchangeRate'),
                })}
              />
            </Form.Item>
          </FormLayoutGroup>

          <div className="mb-6">
            <ExcelTable
              rowKeyColName="id"
              columnDefs={columnDefs}
              data={paymentDetails}
              summaryRowData={summaryRowData}
              includeAddRow={false}
              includeDeleteCol={false}
              includeIdRow={false}
              containerClassName="rounded-lg"
              detailRowAutoHeight={true}
              pagination={false}
              domLayout="autoHeight"
              onDataChange={handleDataChange}
            />
          </div>

          <FormLayoutGroup mdColumns={2}>
            <Form.Item
              name="claimsPaymentSource"
              label={t('claim.paymentReserveChange.claimsPaymentSource')}
            >
              <Select
                size="large"
                placeholder={t('general.selectField', {
                  field: t('claim.paymentReserveChange.claimsPaymentSource'),
                })}
                options={['Float', 'Premium', 'Cash Call'].map((type) => ({
                  label: type,
                  value: type,
                }))}
              />
            </Form.Item>

            {claimsPaymentSource ===
              CreateClaimPaymentPayloadDtoClaimsPaymentSourceEnum.Premium && (
              <Form.Item
                name="premiumBordereauxMonth"
                label={t('claim.paymentReserveChange.premiumBordereauxMonth')}
              >
                <DatePicker
                  picker="month"
                  format={'MM/YYYY'}
                  allowClear
                  size="large"
                  className="w-full"
                  suffixIcon={<CalendarIcon />}
                  placeholder={t('claim.selectDate')}
                />
              </Form.Item>
            )}
          </FormLayoutGroup>

          <FormLayoutGroup mdColumns={2}>
            <Form.Item
              name="fromBankAccount"
              label={t('claim.paymentReserveChange.fromBankAccount')}
            >
              <Select
                size="large"
                placeholder={t('general.selectField', {
                  field: t('claim.paymentReserveChange.fromBankAccount'),
                })}
                loading={isLoadingBankAccounts}
                options={(bankAccounts || []).map((account) => ({
                  label: `${account.accountName} (${account.accountNumber})`,
                  value: account.accountNumber.toString(),
                }))}
              />
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>

        <FormSection
          title={t('claim.paymentReserveChange.comments')}
          collapsible
          status={EFormSectionStatus.FILLING}
        >
          <FormLayoutGroup mdColumns={1}>
            <Form.Item
              name="commentTo"
              label={t('claim.paymentReserveChange.commentTo')}
            >
              <Select
                size="large"
                placeholder={t('general.selectField', {
                  field: t('claim.paymentReserveChange.commentTo'),
                })}
                // TODO: get type from BE
                options={['Binder', 'Broker', 'Internal'].map((type) => ({
                  label: type,
                  value: type,
                }))}
              />
            </Form.Item>
          </FormLayoutGroup>

          <FormLayoutGroup mdColumns={1}>
            <Form.Item
              name="comment"
              label={t('claim.paymentReserveChange.comments')}
            >
              <TextArea
                rows={4}
                placeholder={t('general.enter', {
                  field: t('claim.paymentReserveChange.comments'),
                })}
              ></TextArea>
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>

        <FormSection
          title={t('claim.paymentReserveChange.uploadFiles')}
          collapsible
          status={EFormSectionStatus.FILLING}
        >
          <FormLayoutGroup mdColumns={1}>
            <Form.Item className="mt-6">
              {fileSessionId && (
                <FileUploader
                  sessionId={fileSessionId}
                  entityType={CreateUploadedFilePayloadDtoEntityTypeEnum.Binder}
                  fileList={fileList}
                  setFileList={setFileList}
                />
              )}
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            className="w-full rounded-[12px] h-12 text-base font-medium"
            size="large"
            type="primary"
            variant="outlined"
            color="danger"
            onClick={handleCancel}
            disabled={createClaimPaymentMutation.isLoading}
          >
            {t('general.cancel')}
          </Button>
          <Button
            className="bg-[#1d67b6] w-full rounded-[12px] h-12 text-base font-medium"
            size="large"
            type="primary"
            onClick={handleSave}
            loading={createClaimPaymentMutation.isLoading}
            disabled={!isFormValid || createClaimPaymentMutation.isLoading}
          >
            {t('general.save')}
          </Button>
          <Button
            className="bg-[#1d67b6] w-full rounded-[12px] h-12 text-base font-medium"
            size="large"
            type="primary"
            onClick={() => {}}
            disabled={createClaimPaymentMutation.isLoading}
          >
            {t('general.print')}
          </Button>
        </div>
      </Form>
    </FormContainer>
  );
};

export default PaymentReserveChange;
